import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  NorthEast as NorthEastIcon,
  SouthWest as SouthWestIcon,
} from '@mui/icons-material';

import { Table } from '../../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../../components/table/types';

import { UTCToDateTime } from '../../../../utils/dates';
import { useGetFeeMutation } from '../../../../api';
import { TransactionDirection } from '../../../../enums';
import { useParams } from 'react-router-dom';
import { formatAmount } from '../../../../utils';

type HistoryModalProps = {
  open: boolean;
  onClose: () => void;
  feeId: number | null;
  feeLevelId: number | null;
};

export const FeeHistoryModal: FC<HistoryModalProps> = (props) => {
  const { open, onClose, feeId, feeLevelId } = props;
  const { t } = useTranslation();
  const { fee_group_id } = useParams<'fee_group_id'>();

  // api
  const [getFee, { data: fee, isLoading }] = useGetFeeMutation();

  // fetch fee
  useEffect(() => {
    if (!feeId || !fee_group_id || !feeLevelId) return;

    getFee({ feeId, fee_group_id, fee_level_id: feeLevelId });
  }, [feeId]);

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('fees.modals.history.head-cells.valid-from') },
    { id: '2', label: t('fees.modals.history.head-cells.fixed-amount'), align: 'right' },
    { id: '3', label: t('fees.modals.history.head-cells.option') },
    { id: '4', label: t('fees.modals.history.head-cells.percent'), align: 'right' },
  ];

  const rows: Row[] | undefined = fee?.archived?.map((fee) => {
    const { row_start, fixed_amount, option, percent } = fee;

    const cells: RowCell[] = [
      {
        name: 'row_start',
        value: row_start,
        formatting: (value: string) => {
          return value ? UTCToDateTime(value) : '–';
        },
      },
      { name: 'fixed_amount', value: formatAmount(fixed_amount), align: 'right' },
      { name: 'option', value: option },
      {
        name: 'percent',
        value: percent,
        align: 'right',
        formatting: (value: string) => {
          return `${value} %`;
        },
      },
    ];

    return { cells };
  });

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { minWidth: '380px' } }}>
      <DialogTitle>
        {isLoading ? (
          '–'
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box>{fee?.fee.coin.name}</Box>
            <Stack direction="row" gap="4px" alignItems="center">
              {t(`common.fee-direction.${fee?.fee.type}`)}
            </Stack>
          </Stack>
        )}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        {isLoading ? (
          <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table headCells={headCells} rows={rows} />
        )}
      </DialogContent>
    </Dialog>
  );
};
